import React, { useRef, useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Heading from "../common/heading/Heading"
import "./HomeExperts.css"

const topExperts = [
    {
        name: " Dr. Ram Krishna Sharma",
        designation: "Managing Director, Welsh Education Group",
        expertise: "20+ years of experience in international education and study abroad services.",
        photo: "/images/experts/ram-krishna-sharma.jpg"
    },
    {
        name: "Saurabh Shrivastava",
        designation: "Production & Media Expert",
        expertise: "20+ years of experience in production, TV commercials, and digital campaigns including major film projects.",
        photo: "/images/experts/saurabh-shrivastava.jpeg"
    },
    {
        name: "Vaibhav Parashar",
        designation: "Animation Industry Expert",
        expertise: "21+ years of experience in animation, TV commercials, and mobile gaming with contributions to high-quality animation films.",
        photo: "/images/experts/vaibhav-parashar.jpeg"
    },
    {
        name: "Dr. Raghupathi",
        designation: "Consultant & Advisory, Welsh Education",
        expertise: "More than 33+ years of experience. Serving Chennai, Pondicherry & Delhi regions.",
        photo: "/images/experts/dr-raghupathi.jpg"
    }
];

const otherExperts = [
    {
        name: "Vinay Shukla",
        designation: "Digital Marketing Manager | AVGC Expert",
        expertise: "Expert in performance marketing, creative storytelling, and digital growth strategies.",
        photo: "/images/experts/vinay-shukla.jpg"
    },
    {
        name: "Vikash Khare",
        designation: "Motion Graphics & Visual Storytelling Specialist",
        expertise: "5+ years of experience in motion graphics, visual storytelling, and graphic design.",
        photo: "/images/experts/vikash-khare.jpeg"
    },
    {
        name: "Surendra Prabhakar",
        designation: "Data Science Trainer | AI Specialist",
        expertise: "Specializes in AI, machine learning, and real-world data science training.",
        photo: "/images/experts/surendra-prabhakar.png"
    },
    {
        name: "Kanak Kishore",
        designation: "AI & Machine Learning Specialist",
        expertise: "Focused on practical data science, AI, and analytics training.",
        photo: "/images/experts/kanak-kishore.jpg"
    }
];

const HomeExperts = () => {
    const sliderRef = useRef(null)
    const topSliderRef = useRef(null)
    const [isPaused, setIsPaused] = useState(false)

    const slideLeft = () => {
        sliderRef.current.scrollBy({ left: -320, behavior: 'smooth' })
    }

    const slideRight = () => {
        sliderRef.current.scrollBy({ left: 320, behavior: 'smooth' })
    }

    const topSlideLeft = () => {
        topSliderRef.current.scrollBy({ left: -410, behavior: 'smooth' })
    }

    const topSlideRight = () => {
        if (!topSliderRef.current) return;
        const { scrollLeft, offsetWidth, scrollWidth } = topSliderRef.current;
        // If we are at the end, reset to start
        if (scrollLeft + offsetWidth >= scrollWidth - 10) {
            topSliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
            topSliderRef.current.scrollBy({ left: 410, behavior: 'smooth' });
        }
    }

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isPaused) {
                topSlideRight();
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [isPaused]);

    return (
        <section className="home-experts-section">
            <div className="container">
                <Heading
                    subtitle="Get trained by professionals with real-world experience."
                    title="Our Top Experts"
                />

                {/* HIGHLIGHT SECTION - BIG CARDS SLIDER */}
                <div className="top-experts-carousel-wrapper">
                    <button className="expert-nav-btn left" onClick={topSlideLeft}>
                        <ChevronLeft size={24} />
                    </button>

                    <div 
                        className="top-experts-slider" 
                        ref={topSliderRef}
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                    >
                        {topExperts.map((expert, idx) => (
                            <motion.div
                                key={idx}
                                className="top-expert-card"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                            >
                                <div className="top-expert-image-box">
                                    <img src={expert.photo} alt={expert.name} className="top-expert-photo" />
                                </div>
                                <div className="top-expert-info">
                                    <h3 className="top-expert-name">{expert.name}</h3>
                                    <h4 className="top-expert-designation">{expert.designation}</h4>
                                    <div className="expert-divider"></div>
                                    <p className="top-expert-expertise">{expert.expertise}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <button className="expert-nav-btn right" onClick={topSlideRight}>
                        <ChevronRight size={24} />
                    </button>
                </div>

                <div className="other-experts-header">
                    <h3 className="section-title" style={{ fontSize: '28px', textAlign: 'center', margin: '60px 0 20px', color: '#fff' }}>More Industry Experts</h3>
                </div>

                {/* NORMAL CARDS - SLIDER */}
                <div className="experts-carousel-wrapper">
                    <button className="expert-nav-btn left" onClick={slideLeft}>
                        <ChevronLeft size={24} />
                    </button>

                    <div className="experts-slider" ref={sliderRef}>
                        {otherExperts.map((expert, idx) => (
                            <motion.div
                                key={idx}
                                className="expert-card"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                            >
                                <div className="expert-photo-box">
                                    <img src={expert.photo} alt={expert.name} className="expert-photo" />
                                    <div className="expert-glow"></div>
                                </div>
                                <div className="expert-info">
                                    <h3 className="expert-name">{expert.name}</h3>
                                    <h4 className="expert-designation">{expert.designation}</h4>
                                    <div className="expert-divider"></div>
                                    <p className="expert-expertise">{expert.expertise}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <button className="expert-nav-btn right" onClick={slideRight}>
                        <ChevronRight size={24} />
                    </button>
                </div>
            </div>
        </section>
    )
}

export default HomeExperts
